import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Badge, Boton, Campo, Input, Select } from "@/components/ui";
import { Chips, Dinero, Porcentaje } from "@/components/ui/controles";
import { useToast } from "@/components/ui/Toast";
import { useDatos } from "../store/store";
import { agregarActivo, agregarDeuda, setFila, vaciarFila } from "../store/acciones";
import { activoPorSub, categoriaLlana, inferirTipoDeuda, P, TIPOS_DEUDA, type TipoDeudaId } from "../catalogo-ui";
import { dinero } from "../util";
import { SeccionLayout, Bloque } from "../componentes/SeccionLayout";
import { Ejemplo } from "../componentes/Ejemplo";
import { PanelEnVivo } from "../componentes/PanelEnVivo";
import { ChipBucket, SelectorActivo, type ActivoElegido } from "../componentes/SelectorActivo";
import { useFotoEnVivo } from "../useFotoEnVivo";
import type { LiabilityInput } from "@/engine/types";

function FilaDeuda({ l, cur }: { l: LiabilityInput; cur: string }) {
  const apply = useDatos((x) => x.apply);
  const [tipo, setTipo] = useState<TipoDeudaId>(() => inferirTipoDeuda(l.what, l.personalUse));
  const def = TIPOS_DEUDA.find((t) => t.id === tipo)!;
  const elegir = (t: TipoDeudaId) => {
    const d = TIPOS_DEUDA.find((x) => x.id === t)!;
    setTipo(t);
    const nombreGenerico = !l.what || l.what === "Deuda" || TIPOS_DEUDA.some((x) => x.what === l.what);
    apply(setFila("liabilities", l.row, { personalUse: d.personalUse ?? l.personalUse, ...(nombreGenerico ? { what: d.what || l.what } : {}) }));
  };
  return (
    <li id={`deuda-${l.row}`} className="flex scroll-mt-28 flex-col gap-3 border-b border-borde py-4 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{l.what ?? "Deuda"}</span>
        <button type="button" aria-label={`Quitar ${l.what ?? "deuda"}`} onClick={() => apply(vaciarFila("liabilities", l.row))} className="rounded p-2 text-tinta-suave hover:bg-gray-100"><Trash2 className="h-4 w-4" aria-hidden /></button>
      </div>
      <div>
        <p className="mb-1.5 text-sm font-medium">¿Qué tipo de deuda es?</p>
        <Chips tamano="sm" etiqueta="Tipo de deuda" valor={tipo} onChange={elegir} opciones={TIPOS_DEUDA.map((t) => ({ valor: t.id, etiqueta: t.etiqueta }))} />
        <p className="mt-1.5 text-xs text-tinta-suave">{def.explica}</p>
        {tipo === "otro" && (
          <div className="mt-2"><Chips tamano="sm" etiqueta="¿Es la hipoteca de la casa donde vives?" valor={l.personalUse} onChange={(v) => apply(setFila("liabilities", l.row, { personalUse: v }))}
            opciones={[{ valor: "Sí", etiqueta: "Es de la casa donde vivo" }, { valor: "No", etiqueta: "No" }]} /></div>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="¿Qué debo?" htmlFor={`dw${l.row}`}><Input id={`dw${l.row}`} value={l.what ?? ""} onChange={(e) => apply(setFila("liabilities", l.row, { what: e.target.value }))} /></Campo>
        <Campo etiqueta="Saldo hoy" htmlFor={`db${l.row}`}><Dinero id={`db${l.row}`} valor={l.balance} moneda={cur} onChange={(v) => apply(setFila("liabilities", l.row, { balance: v }))} /></Campo>
        <Campo etiqueta="Pago mensual (opcional)" htmlFor={`dp${l.row}`}><Dinero id={`dp${l.row}`} valor={l.payment} moneda={cur} onChange={(v) => apply(setFila("liabilities", l.row, { payment: v }))} /></Campo>
        <Campo etiqueta="Tasa anual (opcional)" htmlFor={`dr${l.row}`}><Porcentaje id={`dr${l.row}`} valor={l.rate} onChange={(v) => apply(setFila("liabilities", l.row, { rate: v }))} /></Campo>
      </div>
      <p className="text-xs text-tinta-suave">El pago y la tasa no entran a las cuentas; los usa tu asesor para platicar contigo.</p>
    </li>
  );
}

export function Balance() {
  const b = useDatos((x) => x.inputs.balance);
  const inputs = useDatos((x) => x.inputs);
  const cur = inputs.setup.currency === "USD" ? "USD" : "MXN";
  const apply = useDatos((x) => x.apply);
  const toast = useToast();
  const { snapshot } = useFotoEnVivo();
  const [editando, setEditando] = useState<{ row: number | null; inicial: ActivoElegido | null } | null>(null);
  const activos = b.assets.filter((a) => a.what || a.subcategory || a.value !== null);
  const deudas = b.liabilities.filter((l) => l.what || l.balance !== null || l.personalUse !== null);
  const [deudasNuevas, setDeudasNuevas] = useState(0);

  const guardar = (a: ActivoElegido) => {
    if (editando?.row) { apply(setFila("assets", editando.row, a)); return; }
    const r = agregarActivo(useDatos.getState().inputs, { ...a });
    if (r.row === null) { toast.error(`El modelo tiene ${b.assets.length} renglones de activos: agrupa los chicos en uno.`); return; }
    useDatos.getState().apply(() => r.inputs);
  };
  const nuevaDeuda = () => {
    const r = agregarDeuda(useDatos.getState().inputs, { what: "Deuda" });
    if (r.row === null) { toast.error(`El modelo tiene ${b.liabilities.length} renglones de deudas: agrupa las chicas.`); return; }
    useDatos.getState().apply(() => r.inputs);
    setDeudasNuevas((n) => n + 1);
  };
  const T = snapshot?.balance.totals;
  return (
    <div className="grid gap-6 md:grid-cols-[7fr_5fr]">
      <SeccionLayout id="balance" intro="Tus activos y tus pasivos, a valor de hoy. Un balance aproximado y completo vale más que uno exacto a medias.">
        <Bloque id="activos" titulo="Mis activos" accion={<Boton tamano="sm" onClick={() => setEditando({ row: null, inicial: null })}><Plus className="h-4 w-4" aria-hidden /> Agregar</Boton>}>
          <Ejemplo campo="activos" />
          {activos.length === 0 ? (
            <button type="button" onClick={() => setEditando({ row: null, inicial: null })} className="mt-3 flex w-full min-h-20 flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-borde p-4 text-tinta-suave hover:border-primary">
              <Plus className="h-6 w-6" aria-hidden /><span className="font-medium text-tinta">Agregar algo que tengo</span><span className="text-sm">Cuentas, inversiones, tu casa, tu coche, tu negocio…</span>
            </button>
          ) : (
            <ul className="mt-2">
              {activos.map((a) => {
                const c = activoPorSub(a.subcategory);
                const conc = snapshot?.balance.assets.find((x) => x.row === a.row)?.concentration;
                return (
                  <li key={a.row} id={`activo-${a.row}`} className="flex scroll-mt-28 items-start justify-between gap-3 border-b border-borde py-3 last:border-0">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="font-medium">{a.what ?? a.subcategory ?? "Sin nombre"}</span>
                      <span className="text-xs text-tinta-suave">{c ? `${categoriaLlana(c.category)} · ${c.subcategory}` : a.subcategory ? <b className="text-red-800">"{a.subcategory}" no está en el catálogo: edítalo y elige el tipo</b> : <b className="text-amber-800">Falta el tipo</b>}</span>
                      {c && <ChipBucket a={c} />}
                      {conc && <Badge tono="alerta">{conc === "asset" ? "Más de 15% de tu portafolio en un solo activo" : "Más de 30% en un mismo tipo"}</Badge>}
                      {a.bucketOverride && <span className="text-xs text-tinta-suave">Bucket corregido en sala: {a.bucketOverride}</span>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="tabular font-semibold">{a.value === null ? <span className="text-amber-800">sin valor</span> : dinero(a.value, cur)}</span>
                      <span className="flex">
                        <button type="button" aria-label={`Editar ${a.what ?? ""}`} onClick={() => setEditando({ row: a.row, inicial: { what: a.what ?? "", category: a.category ?? "", subcategory: a.subcategory ?? "", value: a.value } })} className="rounded p-2 text-tinta-suave hover:bg-gray-100"><Pencil className="h-4 w-4" aria-hidden /></button>
                        <button type="button" aria-label={`Quitar ${a.what ?? ""}`} onClick={() => apply(vaciarFila("assets", a.row))} className="rounded p-2 text-tinta-suave hover:bg-gray-100"><Trash2 className="h-4 w-4" aria-hidden /></button>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-tinta-suave">Opciones avanzadas (solo en sala): corregir bucket</summary>
            <p className="mt-2 text-xs text-tinta-suave">Solo si tu facilitador te lo pide. No vuelve invertible algo que el catálogo marca como de uso personal o negocio.</p>
            <ul className="mt-2 flex flex-col gap-2">
              {activos.map((a) => (
                <li key={a.row} className="flex items-center justify-between gap-2"><span className="truncate">{a.what}</span>
                  <Select className="max-w-48" value={a.bucketOverride ?? ""} onChange={(e) => apply(setFila("assets", a.row, { bucketOverride: e.target.value || null }))} aria-label={`Corregir bucket de ${a.what}`}>
                    <option value="">Como dice el catálogo</option>{P.lists.buckets.map((x) => <option key={x} value={x}>{x}</option>)}
                  </Select></li>
              ))}
            </ul>
          </details>
        </Bloque>
        <Bloque id="deudas" titulo="Mis pasivos" ayuda="Hipotecas, tarjetas, créditos y préstamos. El tipo decide si se resta de lo que tienes invertido." accion={<Boton tamano="sm" variante="secundario" onClick={nuevaDeuda}><Plus className="h-4 w-4" aria-hidden /> Agregar</Boton>}>
          <Ejemplo campo="deudas" />
          {deudas.length === 0 && deudasNuevas === 0 ? <p className="mt-3 text-sm text-tinta-suave">Si no debes nada, déjalo así.</p> : <ul className="mt-2">{deudas.map((l) => <FilaDeuda key={l.row} l={l} cur={cur} />)}</ul>}
          <div className="mt-3"><Ejemplo campo="deudas.tarjeta" /></div>
        </Bloque>
        {T && T.assets > 0 && (
          <div className="rounded-md border border-borde bg-white p-4 text-sm">
            <p>Patrimonio neto: <b className="tabular">{dinero(T.netWorth, cur)}</b></p>
            <p className="mt-1">Patrimonio invertible neto: <b className="tabular">{dinero(T.investableNet, cur)}</b> <span className="text-tinta-suave">(lo invertible menos tus deudas, salvo la hipoteca de tu casa)</span></p>
          </div>
        )}
      </SeccionLayout>
      <PanelEnVivo />
      <SelectorActivo abierto={!!editando} onCerrar={() => setEditando(null)} inicial={editando?.inicial} onGuardar={guardar} moneda={cur} />
    </div>
  );
}
